import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue3-toastify';
import { useRouter } from 'vue-router';

import capitalMarketsService, { type CreateCessionPayload } from '../services/capitalMarketsService';

export default function useCreateCession() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (payload: CreateCessionPayload[]) => capitalMarketsService.createCession(payload),
    onError: (error: any) => {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      const detail = error?.response?.data?.detail;

      // Verifica se há financiamentos com cessão em progresso
      // Pode estar em 'detail' ou 'message'
      const errorText = detail || message || '';
      const errorTextStr = typeof errorText === 'string' ? errorText : '';

      if (errorTextStr.toLowerCase().includes('cession in progress')) {
        // Extrai os IDs dos financiamentos da mensagem
        // Tenta diferentes padrões: "financing_ids: 123" ou "financing_ids: 123, 456" ou "(financing_ids: 123)"
        const financingIdsMatch = errorTextStr.match(/financing_ids[:\s]+([\d,\s]+)/i);
        let financingIds = '';

        if (financingIdsMatch) {
          financingIds = financingIdsMatch[1]
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id && !Number.isNaN(Number.parseInt(id, 10)))
            .join(', ');
        }

        const warningMessage = financingIds
          ? `Os seguintes financiamentos possuem cessão em progresso e não podem ser incluídos: ${financingIds}.`
          : 'Um ou mais financiamentos possuem cessão em progresso e não podem ser incluídos.';

        toast.warning(warningMessage);
        return;
      }

      const errorMessages: Record<number, string> = {
        400: 'Dados inválidos para criação da cessão.',
        401: 'Não autorizado. Faça login novamente.',
        403: 'Sem permissão para executar esta ação.',
        404: 'Recurso não encontrado.',
        409: 'Conflito ao criar cessão.',
      };

      const errorMessage = errorMessages[status] || message || 'Erro inesperado ao criar cessão.';
      toast.error(errorMessage);
    },
    onSuccess: (data, variables) => {
      if (data && data.length > 0) {
        const firstItem = data[0];
        const isOverturning = variables[0]?.category === 'overturning';
        const entityLabel = isOverturning ? 'Tombamento' : 'Cessão';
        const verb = isOverturning ? 'criado' : 'criada';
        const successMessage = firstItem.message || `${entityLabel} ${verb} com sucesso`;
        toast.success(successMessage);
        queryClient.invalidateQueries({ queryKey: ['cessions'] });
        setTimeout(() => {
          router.push(`/cessions/${firstItem.id}`);
        }, 2000);
      } else {
        toast.error('Resposta inválida do servidor.');
      }
    },
    retry: 1,
    retryDelay: 1000,
  });
}
